<template>
<div>
  <div id="page-panel">
    <div class="customer-top-nav">
      <div class="title">
        <span>Performance ventes - Tous les produits</span><br>
      </div>
      <div class="controls no-print">
        <el-dropdown size="small" type="default" class="">
          <el-button type="primary" size="small" :disabled="rows.length === 0">
            <i class="el-icon-download"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="downloadExcel"><i class="mdi mdi-file-excel"/> Excel</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <div style="margin-top:8px;display:flex;gap:16px;flex-wrap:wrap;align-items:center;">
      <span style="font-size:12px;margin-right:0;color:#606266;">Période :</span>
      <el-select v-model="periodMonths" size="small" style="width:180px;" @change="fetchData">
        <el-option :value="6" label="6 derniers mois"></el-option>
        <el-option :value="12" label="12 derniers mois"></el-option>
        <el-option :value="18" label="18 derniers mois"></el-option>
        <el-option :value="24" label="24 derniers mois"></el-option>
        <el-option :value="36" label="36 derniers mois"></el-option>
      </el-select>
      <span style="font-size:12px;color:#606266;">Délai :</span>
      <el-select v-model="thresholdDays" size="small" style="width:120px;" @change="fetchData">
        <el-option :value="30" label="30 jours"></el-option>
        <el-option :value="90" label="90 jours"></el-option>
        <el-option :value="120" label="120 jours"></el-option>
        <el-option :value="150" label="150 jours"></el-option>
        <el-option :value="180" label="180 jours"></el-option>
        <el-option :value="210" label="210 jours"></el-option>
        <el-option :value="240" label="240 jours"></el-option>
        <el-option :value="270" label="270 jours"></el-option>
        <el-option :value="300" label="300 jours"></el-option>
        <el-option :value="330" label="330 jours"></el-option>
        <el-option :value="360" label="360 jours"></el-option>
      </el-select>
    </div>

    <div style="margin-top: 12px; display: flex; gap: 12px; flex-wrap: wrap; align-items: center;">
      <span style="font-size:12px;color:#606266;">Rechercher</span>
      <el-input
        v-model="searchQuery"
        clearable
        size="small"
        placeholder="Rechercher par produit, SKU ou producteur"
        style="width: 300px;">
      </el-input>
      <el-checkbox-group
        v-model="performanceFilterSelection"
        @change="handlePerformanceFilterChange">
        <el-checkbox-button label="best">
          Plus performants
        </el-checkbox-button>
        <el-checkbox-button label="low">
          Moins performants
        </el-checkbox-button>
      </el-checkbox-group>
    </div>

    <div class="stats-grid" style="margin-top: 14px;">
      <div class="stats-card">
        <div class="stats-title stats-title-with-action">
          <span>Performance des ventes ({{ thresholdDays }} jours)</span>
          <el-button
            v-if="statsFilter.group === 'salesQuality'"
            type="text"
            size="mini"
            @click="clearStatsFilter">
            Tout afficher
          </el-button>
        </div>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('salesQuality', 'good') }"
          @click="toggleStatsFilter('salesQuality', 'good')">
          <span class="stats-label">Bonne (>= 80%)</span>
          <span class="stats-value pct-high">{{ salesQualityStats.good }}</span>
        </button>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('salesQuality', 'medium') }"
          @click="toggleStatsFilter('salesQuality', 'medium')">
          <span class="stats-label">Moyenne (&lt; 80% et &gt;= 50%)</span>
          <span class="stats-value pct-medium">{{ salesQualityStats.medium }}</span>
        </button>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('salesQuality', 'bad') }"
          @click="toggleStatsFilter('salesQuality', 'bad')">
          <span class="stats-label">Mauvaise (&lt; 50%)</span>
          <span class="stats-value pct-low">{{ salesQualityStats.bad }}</span>
        </button>
      </div>

      <div class="stats-card">
        <div class="stats-title stats-title-with-action">
          <span>Concentration des ventes</span>
          <el-button
            v-if="statsFilter.group === 'concentration'"
            type="text"
            size="mini"
            @click="clearStatsFilter">
            Tout afficher
          </el-button>
        </div>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('concentration', 'low') }"
          @click="toggleStatsFilter('concentration', 'low')">
          <span class="stats-label">Faible</span>
          <span class="stats-value risk-low">{{ concentrationStats.low }}</span>
        </button>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('concentration', 'medium') }"
          @click="toggleStatsFilter('concentration', 'medium')">
          <span class="stats-label">Moderee</span>
          <span class="stats-value risk-medium">{{ concentrationStats.medium }}</span>
        </button>
        <button
          type="button"
          class="stats-row stats-link-row"
          :class="{ 'stats-link-row-active': isStatsFilterActive('concentration', 'high') }"
          @click="toggleStatsFilter('concentration', 'high')">
          <span class="stats-label">Elevee</span>
          <span class="stats-value risk-high">{{ concentrationStats.high }}</span>
        </button>
      </div>
    </div>

    <el-divider></el-divider>

    <el-table
      v-loading="loading"
      :data="displayedRows"
      border
      style="width: 100%; font-size:12px;"
      empty-text="Aucune donnée pour cette période"
      @sort-change="handleSortChange">
      <el-table-column prop="sku" label="SKU" width="130" sortable="custom" />
      <el-table-column prop="name" label="Produit" min-width="280" sortable="custom">
        <template slot-scope="scope">
          <router-link :to="getPerformanceReportLink(scope.row)" target="_blank">
            {{ scope.row.name }}&nbsp;<i class="el-icon-top-right" style="font-size:11px;"></i>
          </router-link>
          <div style="font-size:11px; color:#909399; margin-top:2px;">
            {{ [scope.row.uvc ? 'UVC\u00a0' + scope.row.uvc : null, scope.row.lblFormat].filter(Boolean).join(' | ') }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="provider_name" label="Producteur" min-width="180" sortable="custom" />
      <el-table-column prop="last_availability_date" label="Derniere mise en disponibilite" width="175" sortable="custom">
        <template slot-scope="scope">
          {{ formatDate(scope.row.last_availability_date) }}
        </template>
      </el-table-column>
      <el-table-column :prop="'percent_threshold'" :label="'% vendu <=' + thresholdDays + 'j'" width="150" align="right" sortable="custom">
        <template slot-scope="scope">
          <span :class="percentClass(scope.row.percent_threshold)">{{ formatPercent(scope.row.percent_threshold) }}%</span>
        </template>
      </el-table-column>
      <el-table-column prop="hhi_score" label="Indice Herfindahl (0-100)" width="190" align="right" sortable="custom">
        <template slot-scope="scope">
          <span :class="riskClass(scope.row.hhi_score)">{{ formatPercent(scope.row.hhi_score) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Niveau concentration" width="170" align="center" sortable="custom" prop="hhi_label">
        <template slot-scope="scope">
          <span :class="riskClass(scope.row.hhi_score)">{{ hhiLabel(scope.row.hhi_score) }}</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import InventoryAPI from '@/services/API/inventory';
import dayjs from 'dayjs';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  name: 'WineSalesProductsPerformanceReport',
  props : {
    width : {
      type : Number,
      default : 100
    },
    fiscalEndDay : Number,
    fiscalEndMonth : Number,
    lastFiscalYearDate : Object,
    fiscalYearDate : Object,
    month : Object,
    lastMonth : Object,
    companyName : '',
  },
  data() {
    return {
      loading: false,
      periodMonths: 12,
      thresholdDays: 120,
      rows: [],
      searchQuery: '',
      performanceFilterSelection: [],
      statsFilter: {
        group: null,
        value: null,
      },
      sortProp: 'percent_threshold',
      sortOrder: 'descending',
    };
  },
  computed: {
    myInventory() {
      return _.find(this.inventories, inv => inv.code === 'IP');
    },
    periodStartDate() {
      return dayjs().subtract(this.periodMonths, 'month').startOf('month').format('YYYY-MM-DD Z');
    },
    periodEndDate() {
      return dayjs().endOf('month').format('YYYY-MM-DD Z');
    },
    filteredRows() {
      const searchQuery = this.normalizeSearchValue(this.searchQuery);
      const activePerformanceFilter = _.head(this.performanceFilterSelection);
      const filteredByPerformance = _.filter(this.rows, (row) => {
        const isBestProduct = Number(row.hhi_score) < 30 && Number(row.percent_threshold) >= 80;
        const isLowPerformanceProduct = Number(row.hhi_score) > 50 && Number(row.percent_threshold) < 50;

        if (activePerformanceFilter === 'best') return isBestProduct;
        if (activePerformanceFilter === 'low') return isLowPerformanceProduct;
        return true;
      });

      const filteredByStats = _.filter(filteredByPerformance, row => this.matchesStatsFilter(row));

      if (searchQuery === '') return filteredByStats;

      return _.filter(filteredByStats, (row) => {
        const searchableValues = [
          _.get(row, 'name', ''),
          _.get(row, 'sku', ''),
          _.get(row, 'provider_name', ''),
        ];

        return _.some(searchableValues, value => this.normalizeSearchValue(value).includes(searchQuery));
      });
    },
    displayedRows() {
      return this.sortedRows(this.filteredRows);
    },
    concentrationStats() {
      return _.reduce(this.rows, (acc, row) => {
        const score = Number(row.hhi_score || 0);
        if (score >= 60) {
          acc.high += 1;
        } else if (score >= 30) {
          acc.medium += 1;
        } else {
          acc.low += 1;
        }
        return acc;
      }, { low: 0, medium: 0, high: 0 });
    },
    salesQualityStats() {
      return _.reduce(this.rows, (acc, row) => {
        const pct = Number(row.percent_threshold || 0);
        if (pct >= 80) {
          acc.good += 1;
        } else if (pct >= 50) {
          acc.medium += 1;
        } else {
          acc.bad += 1;
        }
        return acc;
      }, { good: 0, medium: 0, bad: 0 });
    },
    ...mapState('inventory', ['inventories']),
  },
  methods: {
    ...mapActions('inventory', ['fetchInventories']),
    normalizeSearchValue(value) {
      return _.trim(_.toLower(_.deburr(String(value || ''))));
    },
    formatNumber(value) {
      return Math.round(Number(value || 0) * 100) / 100;
    },
    formatPercent(value) {
      return Math.round(Number(value || 0));
    },
    formatDate(value) {
      return value ? dayjs(value).format('YYYY-MM-DD') : '';
    },
    riskClass(score) {
      if (Number(score) >= 60) return 'risk-high';
      if (Number(score) >= 30) return 'risk-medium';
      return 'risk-low';
    },
    percentClass(pct) {
      if (Number(pct) >= 80) return 'pct-high';
      if (Number(pct) >= 50) return 'pct-medium';
      return 'pct-low';
    },
    hhiLabel(score) {
      if (Number(score) >= 60) return 'Concentration elevee';
      if (Number(score) >= 30) return 'Concentration moderee';
      return 'Concentration faible';
    },
    matchesStatsFilter(row) {
      const score = Number(row.hhi_score || 0);
      const pct = Number(row.percent_threshold || 0);

      if (!this.statsFilter.group || !this.statsFilter.value) return true;

      if (this.statsFilter.group === 'concentration') {
        if (this.statsFilter.value === 'low') return score < 30;
        if (this.statsFilter.value === 'medium') return score >= 30 && score < 60;
        if (this.statsFilter.value === 'high') return score >= 60;
      }

      if (this.statsFilter.group === 'salesQuality') {
        if (this.statsFilter.value === 'good') return pct >= 80;
        if (this.statsFilter.value === 'medium') return pct >= 50 && pct < 80;
        if (this.statsFilter.value === 'bad') return pct < 50;
      }

      return true;
    },
    toggleStatsFilter(group, value) {
      if (this.statsFilter.group === group && this.statsFilter.value === value) {
        this.clearStatsFilter();
        return;
      }
      this.statsFilter = { group, value };
    },
    handlePerformanceFilterChange(values) {
      if (values.length <= 1) return;
      this.performanceFilterSelection = [_.last(values)];
    },
    clearStatsFilter() {
      this.statsFilter = {
        group: null,
        value: null,
      };
    },
    isStatsFilterActive(group, value) {
      return this.statsFilter.group === group && this.statsFilter.value === value;
    },
    normalizeRows(rows) {
      return _.map(rows, (r) => ({
        ...r,
        percent_threshold: Number(r.percent_threshold || 0),
        percent_150: Number(r.percent_150 || 0),
        hhi_score: Number(r.hhi_score || 0),
        hhi_label: this.hhiLabel(Number(r.hhi_score || 0)),
      }));
    },
    sortedRows(rows) {
      if (!this.sortProp || !this.sortOrder) return rows;
      const order = this.sortOrder === 'ascending' ? 'asc' : 'desc';
      const numericSortProps = ['percent_threshold', 'percent_150', 'hhi_score'];
      if (_.includes(numericSortProps, this.sortProp)) {
        return _.orderBy(rows, [(row) => Number(_.get(row, this.sortProp) || 0)], [order]);
      }
      if (this.sortProp === 'last_availability_date') {
        return _.orderBy(rows, [(row) => dayjs(_.get(row, 'last_availability_date')).valueOf() || 0], [order]);
      }
      return _.orderBy(rows, [this.sortProp], [order]);
    },
    handleSortChange({ prop, order }) {
      if (order == null) {
        this.sortProp = 'percent_threshold';
        this.sortOrder = 'descending';
      } else {
        this.sortProp = prop;
        this.sortOrder = order;
      }
    },
    getPerformanceReportLink(row) {
      return {
        name: 'report',
        params: {
          code: 'PERFORMANCE_PRODUCT',
        },
        query: {
          puid: row.puid,
          months: this.periodMonths,
        },
      };
    },
    fetchData() {
      if (!_.get(this.myInventory, 'id')) {
        this.rows = [];
        return Promise.resolve();
      }

      this.loading = true;
      return InventoryAPI.fetchProductsPerformanceSummary(this.myInventory.id, {
        startDate: this.periodStartDate,
        endDate: this.periodEndDate,
        threshold: this.thresholdDays,
      })
        .then((data) => {
          this.rows = this.normalizeRows(data);
          this.loading = false;
        })
        .catch(() => {
          this.rows = [];
          this.loading = false;
          this.$message.error('Une erreur systeme est survenue. Veuillez reessayer plus tard');
        });
    },
    downloadExcel() {
      const wb = XLSX.utils.book_new();
      const exportRows = _.map(this.displayedRows, (row) => ({
        SKU: row.sku,
        Produit: row.name,
        Producteur: row.provider_name,
        UVC: row.uvc,
        Format: row.lblFormat,
        'Derniere mise en disponibilite': this.formatDate(row.last_availability_date),
        [`% vendu <=${this.thresholdDays}j`]: this.formatPercent(row.percent_threshold),
        'Indice Herfindahl (0-100)': this.formatPercent(row.hhi_score),
        'Niveau concentration': row.hhi_label,
      }));

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Performance Produits');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const fileName = `performance-produits-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
    },
  },
  created() {
    this.fetchInventories().then(() => this.fetchData());
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../assets/scss/_variables';

.title {
  font-size: 1.6em;
  font-weight: bold;
  margin-right: 20px;
  position: relative;
  float: left;
}
.customer-top-nav {
  height: 40px;
}

.controls {
  position: relative;
  float: right;

  button {
    margin-left: 20px;
  }
}

.risk-low { color: #67c23a; }
.risk-medium { color: #e6a23c; }
.risk-high { color: #f56c6c; }

.pct-high { color: #67c23a; font-weight: 600; }
.pct-medium { color: #e6a23c; font-weight: 600; }
.pct-low { color: #f56c6c; font-weight: 600; }

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
}

.stats-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  padding: 10px 12px;
}

.stats-title {
  font-size: 12px;
  font-weight: 700;
  color: #606266;
  margin-bottom: 6px;
}

.stats-title-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  margin-top: 3px;
}

.stats-label {
  color: #606266;
}

.stats-value {
  font-weight: 700;
}

.stats-link-row {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.stats-link-row:hover {
  background: #f2f6fc;
}

.stats-link-row-active {
  background: #ecf5ff;
  border-radius: 4px;
}
</style>
